import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connectSession, listAccounts, onboardComplete, onboardStart } from "../api/ctraderApi";

const POPUP_FEATURES = () => {
  const width = Number(import.meta.env.VITE_CTRADER_POPUP_WIDTH || 600);
  const height = Number(import.meta.env.VITE_CTRADER_POPUP_HEIGHT || 700);
  const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);
  return `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars`;
};

export default function useCTraderOnboarding({ autoConnect = true, messageTimeoutMs = 5 * 60 * 1000 } = {}) {
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [stateToken, setStateToken] = useState(null);
  const [tempId, setTempId] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [ctidUserId, setCtidUserId] = useState(null);
  const [selected, setSelected] = useState(null);

  const popupRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleMessage = useCallback((e) => {
    try {
      if (!e || !e.data) return;
      const { source, temp_id, ctrader_temp_id /*, state */ } = e.data || {};
      if (source !== "ctrader") return;
      const receivedTempId = temp_id || ctrader_temp_id; // require a real temp_id
      if (!receivedTempId) return; // don't use state as key in grantingaccess flow
      // Note: origin/state checks removed for debugging
      setTempId(receivedTempId);
      setStatus("loadingAccounts");
      if (popupRef.current && !popupRef.current.closed) {
        try { popupRef.current.close(); } catch (_) {}
      }
    } catch (err) {
      setError(err);
      setStatus("error");
    }
  }, []);

  const cleanup = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      try { popupRef.current.close(); } catch (_) {}
    }
    popupRef.current = null;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      cleanup();
    };
  }, [handleMessage, cleanup]);

  useEffect(() => {
    const run = async () => {
      if (status !== "loadingAccounts" || !tempId) return;
      try {
  const data = await listAccounts(tempId);
  // Some backends return a new temp_id; prefer it if present
  if (data?.temp_id) setTempId(data.temp_id);
  // ctid_user_id might be null or may use different casing
  setCtidUserId(data.ctid_user_id ?? data.ctidUserId ?? null);
        setAccounts(data.accounts || []);
        setStatus("selecting");
      } catch (err) {
        setError(err);
        setStatus("error");
      }
    };
    run();
  }, [status, tempId]);

  const start = useCallback(async (internalAccountId) => {
    setError(null);
    setSelected(null);
    setAccounts([]);
    setCtidUserId(null);
    setTempId(null);
    setStatus("starting");

    try {
      const data = await onboardStart(internalAccountId); // { authorize_url, state }
      const { authorize_url, state } = data || {};
      if (!authorize_url) throw new Error("Missing authorize_url");
      setStateToken(state || null);

      const popup = window.open(authorize_url, "ctrader_oauth", POPUP_FEATURES());
      if (!popup) {
        setStatus("awaitingPopup");
        throw new Error("Popup blocked. Please allow popups and try again.");
      }
      popupRef.current = popup;
      setStatus("awaitingTempId");

      if (messageTimeoutMs > 0) {
        timeoutRef.current = setTimeout(() => {
          if (status === "awaitingTempId") {
            setError(new Error("Timed out waiting for authorization."));
            setStatus("error");
            cleanup();
          }
        }, messageTimeoutMs);
      }
      return { ok: true };
    } catch (err) {
      setError(err);
      if (status === "starting") setStatus("error");
      return { ok: false, error: err };
    }
  }, [cleanup, messageTimeoutMs, status]);

  const chooseAccount = useCallback(async (internalAccountId, { ctrader_account_id, environment }) => {
    // tempId is required; ctidUserId may be optional depending on backend
    if (!tempId) return { ok: false, error: new Error("Missing tempId") };
    setStatus("completing");
    try {
      const payload = {
        temp_id: tempId,
        ctrader_account_id,
        environment,
      };
      if (ctidUserId) payload.ctid_user_id = ctidUserId;
      await onboardComplete(internalAccountId, payload);
      if (autoConnect) {
        try { await connectSession(internalAccountId); } catch (_) {}
      }
      setSelected({ ctrader_account_id, environment });
      setStatus("done");
      return { ok: true };
    } catch (err) {
      setError(err);
      setStatus("error");
      return { ok: false, error: err };
    }
  }, [autoConnect, ctidUserId, tempId]);

  const cancel = useCallback(() => {
    setStatus("canceled");
    cleanup();
  }, [cleanup]);

  return {
    status,
    error,
    accounts,
    ctidUserId,
    tempId,
    selected,
    start,
    cancel,
    chooseAccount,
  };
}
